import {
  JUMP_PERFECT_MAX_CLEARANCE,
  JUMP_PERFECT_MIN_CLEARANCE,
  type JumpChart,
} from '@shared/jumpChart';
import {
  decodeJumpGrid,
  jumpColCentreBeat,
  JUMP_GROUND_Y,
  type JumpGridSpike,
  type JumpTerrainSpan,
} from '@shared/jumpGrid';
import type { JumpLayout } from '../jumpPlayfieldGeometry';
import { obstacleCanvasX, playfieldYToCanvas } from '../jumpPlayfieldGeometry';
import {
  CEILING_FILL_ALPHA,
  CEILING_LIP_HEIGHT,
  CEILING_TOOTH_DROP,
  CEILING_TOOTH_SPACING,
  OBSTACLE_DRAW_BEATS_AHEAD,
  OBSTACLE_DRAW_BEATS_BEHIND,
  OBSTACLE_HINT_FADE_BEATS,
  OBSTACLE_HINT_FONT_PX,
  OBSTACLE_HINT_GAP,
  OBSTACLE_HINT_MAX_ALPHA,
  OBSTACLE_TELEGRAPH_BEATS,
  OBSTACLE_TELEGRAPH_BOB_AMP,
  OBSTACLE_TELEGRAPH_BOB_BASE,
  OBSTACLE_TELEGRAPH_HALF_WIDTH,
  OBSTACLE_TELEGRAPH_HEIGHT,
  OBSTACLE_TELEGRAPH_MAX_ALPHA,
  OBSTACLE_TELEGRAPH_PULSE_PER_BEAT,
  PERFECT_ZONE_COLOUR,
  PERFECT_ZONE_EDGE_ALPHA,
  PERFECT_ZONE_FADE_BEATS,
  PERFECT_ZONE_FILL_ALPHA,
  PERFECT_ZONE_HALF_WIDTH,
  PERFECT_ZONE_PULSE_DEPTH,
  SAW_SPIN_RAD_PER_BEAT,
  SAW_TOOTH_COUNT,
  SAW_TOOTH_TIP_MUL,
  SPIKE_BASE_WIDTH,
} from './jumpRenderConstants';
import { hexToRgba } from './jumpRenderColour';

const TWO_PI = Math.PI * 2;

/**
 * Columns holding a LONE spike (no spike one column either side). Memoised on
 * the decoded spike array — itself WeakMap-cached per grid by
 * {@link decodeJumpGrid} — so the per-frame obstacle draw never rebuilds it.
 */
const loneSpikeColsCache = new WeakMap<JumpGridSpike[], Set<number>>();
function loneSpikeCols(spikes: JumpGridSpike[]): Set<number> {
  const cached = loneSpikeColsCache.get(spikes);
  if (cached) {
    return cached;
  }
  const all = new Set<number>();
  for (const s of spikes) {
    all.add(s.col);
  }
  const lone = new Set<number>();
  for (const s of spikes) {
    if (!all.has(s.col - 1) && !all.has(s.col + 1)) {
      lone.add(s.col);
    }
  }
  loneSpikeColsCache.set(spikes, lone);
  return lone;
}

/**
 * Draws the scrolling hazards a Jump grid compiles to: ground spikes / spike
 * walls (lethal triangles standing on the local surface), floating sawblades
 * (jump-over / duck-under lethal discs), overhead ceiling slabs and hanging
 * stalactites (duck-under kill-bounds), plus the grid's coaching pills. Terrain
 * bodies + their lethal step faces are the scene pass's job ({@link
 * import('./jumpDrawScene').JumpSceneRenderer}); this pass is everything that
 * sits ON the terrain.
 *
 * Stateless: hazards come from the deterministic, WeakMap-cached
 * {@link decodeJumpGrid}, so visuals always match the engine's hazard model and
 * there's no per-instance cursor to keep in sync. Hazard counts are modest
 * (≤ ~100 even on a dense Extreme grid) so a per-frame beat-window scan is cheap.
 *
 * When `telegraph` is true (disabled under reduced motion) a spike / saw within
 * {@link OBSTACLE_TELEGRAPH_BEATS} of the cube's column gets a pulsing accent
 * chevron. Purely cosmetic — no engine / scoring impact.
 */
export function drawJumpObstacles(
  ctx: CanvasRenderingContext2D,
  layout: JumpLayout,
  chart: JumpChart,
  beat: number,
  visibleBeats: number,
  chromeShadows: boolean,
  telegraph: boolean,
): void {
  const decoded = decodeJumpGrid(chart.grid);
  const palette = chart.palette;
  const minBeat = beat - OBSTACLE_DRAW_BEATS_BEHIND;
  const maxBeat = beat + OBSTACLE_DRAW_BEATS_AHEAD;
  const halfBase = (SPIKE_BASE_WIDTH / 2) * layout.scaleX;
  const pulseWave = 0.5 + 0.5 * Math.sin(beat * Math.PI * 2 * OBSTACLE_TELEGRAPH_PULSE_PER_BEAT);

  // Only LONE spikes (no neighbour either side → not a wide spike-wall, which
  // forces the full jump and so has no perfect window) get a perfect zone drawn;
  // saws are always lone. Memoised per cached decode so this draw stays
  // allocation-free per frame.
  const loneCols = loneSpikeCols(decoded.spikes);

  ctx.save();

  for (const spike of decoded.spikes) {
    if (spike.beat < minBeat || spike.beat > maxBeat) {
      continue;
    }
    const cx = obstacleCanvasX(layout, beat, spike.beat, visibleBeats);
    const tipY = playfieldYToCanvas(layout, spike.tipY);
    const baseY = playfieldYToCanvas(layout, spike.baseY);
    if (loneCols.has(spike.col)) {
      drawPerfectZone(ctx, layout, cx, spike.tipY, spike.beat - beat, pulseWave, chromeShadows);
    }
    drawSpike(ctx, cx, baseY, tipY, halfBase, palette.hazard, chromeShadows);
    if (telegraph) {
      drawTelegraph(ctx, layout, cx, tipY, spike.beat - beat, pulseWave, palette.player, chromeShadows);
    }
  }

  for (const saw of decoded.saws) {
    if (saw.beat < minBeat || saw.beat > maxBeat) {
      continue;
    }
    const cx = obstacleCanvasX(layout, beat, saw.beat, visibleBeats);
    const topY = playfieldYToCanvas(layout, saw.topY);
    const botY = playfieldYToCanvas(layout, saw.bottomY);
    drawPerfectZone(ctx, layout, cx, saw.topY, saw.beat - beat, pulseWave, chromeShadows);
    drawSaw(ctx, cx, topY, botY, beat, palette.hazard, chromeShadows);
    if (telegraph) {
      drawTelegraph(ctx, layout, cx, topY, saw.beat - beat, pulseWave, palette.player, chromeShadows);
    }
  }

  for (const ov of decoded.overheads) {
    if (ov.endBeat < minBeat || ov.startBeat > maxBeat) {
      continue;
    }
    const x0 = obstacleCanvasX(layout, beat, ov.startBeat, visibleBeats);
    const x1 = obstacleCanvasX(layout, beat, ov.endBeat, visibleBeats);
    const underY = playfieldYToCanvas(layout, ov.bottomY);
    if (ov.kind === 'stalactite') {
      drawStalactite(ctx, x0, x1, playfieldYToCanvas(layout, ov.topY), underY, palette.hazard, chromeShadows);
    } else {
      drawCeiling(ctx, x0, x1, underY, layout, palette.hazard, chromeShadows);
    }
  }

  if (chart.grid.hints) {
    for (const hint of chart.grid.hints) {
      const hintBeat = jumpColCentreBeat(chart.grid, hint.col);
      if (hintBeat < minBeat - 1 || hintBeat > maxBeat) {
        continue;
      }
      const cx = obstacleCanvasX(layout, beat, hintBeat, visibleBeats);
      const surfaceY = playfieldYToCanvas(layout, terrainTopYAt(decoded.terrain, hintBeat));
      drawObstacleHint(ctx, layout, cx, surfaceY, false, hint.text, hintBeat - beat, palette, chromeShadows);
    }
  }

  ctx.restore();
}

/** Floor-top Y of the terrain span covering `beat` (baseline if none / a pit). */
function terrainTopYAt(terrain: ReadonlyArray<JumpTerrainSpan>, beat: number): number {
  for (const span of terrain) {
    if (!span.pit && beat >= span.startBeat && beat < span.endBeat) {
      return span.topY;
    }
  }
  return JUMP_GROUND_Y;
}

/**
 * Pulsing downward chevron hovering above an upcoming hazard, fading in as it
 * approaches the cube's column (`beatsAway` → 0) and bobbing with the shared
 * beat pulse. Only the leading approach is telegraphed; once the hazard
 * reaches/passes the cube (`beatsAway <= 0`) the cue is gone.
 */
function drawTelegraph(
  ctx: CanvasRenderingContext2D,
  layout: JumpLayout,
  x: number,
  topCanvasY: number,
  beatsAway: number,
  pulseWave: number,
  accent: string,
  chromeShadows: boolean,
): void {
  if (beatsAway <= 0 || beatsAway > OBSTACLE_TELEGRAPH_BEATS) {
    return;
  }
  const proximity = 1 - beatsAway / OBSTACLE_TELEGRAPH_BEATS;
  const alpha = proximity * OBSTACLE_TELEGRAPH_MAX_ALPHA * (0.55 + 0.45 * pulseWave);
  if (alpha <= 0.02) {
    return;
  }
  const halfW = OBSTACLE_TELEGRAPH_HALF_WIDTH * layout.scaleX;
  const heightPx = OBSTACLE_TELEGRAPH_HEIGHT * layout.scaleY;
  const bob = (OBSTACLE_TELEGRAPH_BOB_BASE + pulseWave * OBSTACLE_TELEGRAPH_BOB_AMP) * layout.scaleY;
  const baseY = topCanvasY - bob;
  ctx.save();
  ctx.globalAlpha = Math.min(1, alpha);
  ctx.fillStyle = accent;
  if (chromeShadows) {
    ctx.shadowColor = accent;
    ctx.shadowBlur = 8 * proximity;
  }
  ctx.beginPath();
  ctx.moveTo(x - halfW, baseY - heightPx);
  ctx.lineTo(x + halfW, baseY - heightPx);
  ctx.lineTo(x, baseY);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Translucent "perfect zone" band floated above a lone spike / saw, marking the
 * [{@link JUMP_PERFECT_MIN_CLEARANCE}, {@link JUMP_PERFECT_MAX_CLEARANCE}]
 * clearance window the cube's underside must thread (over the obstacle, on the
 * beat) to grade `perfect`. `obstacleTopY` is the obstacle's lethal top in
 * playfield units (spike tip / saw top); the band sits from there up by [MIN,
 * MAX]. Fades in with approach proximity and breathes with the beat pulse, then
 * vanishes as it reaches the cube — purely a coaching overlay, no scoring tie.
 */
function drawPerfectZone(
  ctx: CanvasRenderingContext2D,
  layout: JumpLayout,
  x: number,
  obstacleTopY: number,
  beatsAway: number,
  pulseWave: number,
  chromeShadows: boolean,
): void {
  if (beatsAway <= -0.25 || beatsAway > PERFECT_ZONE_FADE_BEATS) {
    return;
  }
  const proximity = Math.min(1, Math.max(0, 1 - beatsAway / PERFECT_ZONE_FADE_BEATS));
  const pulse = 1 - PERFECT_ZONE_PULSE_DEPTH + PERFECT_ZONE_PULSE_DEPTH * pulseWave;
  const fillAlpha = PERFECT_ZONE_FILL_ALPHA * proximity * pulse;
  if (fillAlpha <= 0.01) {
    return;
  }
  const topY = playfieldYToCanvas(layout, obstacleTopY - JUMP_PERFECT_MAX_CLEARANCE);
  const botY = playfieldYToCanvas(layout, obstacleTopY - JUMP_PERFECT_MIN_CLEARANCE);
  const halfW = PERFECT_ZONE_HALF_WIDTH * layout.scaleX;
  const h = botY - topY;
  ctx.save();
  ctx.globalAlpha = fillAlpha;
  ctx.fillStyle = PERFECT_ZONE_COLOUR;
  if (chromeShadows) {
    ctx.shadowColor = PERFECT_ZONE_COLOUR;
    ctx.shadowBlur = 12 * proximity;
  }
  roundRect(ctx, x - halfW, topY, halfW * 2, h, Math.min(halfW, h / 2));
  ctx.fill();
  ctx.shadowBlur = 0;
  // Crisp top + bottom rails so the window's edges read clearly.
  ctx.globalAlpha = Math.min(1, PERFECT_ZONE_EDGE_ALPHA * proximity * pulse);
  ctx.strokeStyle = PERFECT_ZONE_COLOUR;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x - halfW, topY);
  ctx.lineTo(x + halfW, topY);
  ctx.moveTo(x - halfW, botY);
  ctx.lineTo(x + halfW, botY);
  ctx.stroke();
  ctx.restore();
}

/** Lethal ground spike (or one tooth of a wall) — a triangle from `baseY` to `tipY`. */
function drawSpike(
  ctx: CanvasRenderingContext2D,
  cx: number,
  baseY: number,
  tipY: number,
  halfBase: number,
  colour: string,
  chromeShadows: boolean,
): void {
  ctx.fillStyle = colour;
  if (chromeShadows) {
    ctx.shadowColor = colour;
    ctx.shadowBlur = 10;
  }
  ctx.beginPath();
  ctx.moveTo(cx - halfBase, baseY);
  ctx.lineTo(cx, tipY);
  ctx.lineTo(cx + halfBase, baseY);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  // Inner highlight stripe for a faceted look.
  ctx.strokeStyle = hexToRgba('#ffffff', 0.55);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx, tipY);
  ctx.lineTo(cx - halfBase * 0.35, baseY);
  ctx.stroke();
}

/**
 * Floating sawblade: a spinning toothed disc filling the lethal `[topY, bottomY]`
 * region. Spin is beat-locked (deterministic with the scroll), purely cosmetic.
 */
function drawSaw(
  ctx: CanvasRenderingContext2D,
  cx: number,
  topY: number,
  bottomY: number,
  beat: number,
  colour: string,
  chromeShadows: boolean,
): void {
  const cy = (topY + bottomY) / 2;
  const r = Math.max(4, (bottomY - topY) / 2);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(beat * SAW_SPIN_RAD_PER_BEAT);
  ctx.fillStyle = colour;
  if (chromeShadows) {
    ctx.shadowColor = colour;
    ctx.shadowBlur = 12;
  }
  ctx.beginPath();
  for (let i = 0; i < SAW_TOOTH_COUNT; i += 1) {
    const a0 = (i / SAW_TOOTH_COUNT) * TWO_PI;
    const aTip = ((i + 0.5) / SAW_TOOTH_COUNT) * TWO_PI;
    const a1 = ((i + 1) / SAW_TOOTH_COUNT) * TWO_PI;
    ctx.lineTo(Math.cos(a0) * r, Math.sin(a0) * r);
    ctx.lineTo(Math.cos(aTip) * r * SAW_TOOTH_TIP_MUL, Math.sin(aTip) * r * SAW_TOOTH_TIP_MUL);
    ctx.lineTo(Math.cos(a1) * r, Math.sin(a1) * r);
  }
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  // Dark hub + bright bolt so the spin reads.
  ctx.fillStyle = hexToRgba('#05070d', 0.92);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.42, 0, TWO_PI);
  ctx.fill();
  ctx.fillStyle = hexToRgba('#ffffff', 0.7);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.16, 0, TWO_PI);
  ctx.fill();
  ctx.restore();
}

/**
 * Overhead ceiling slab spanning `[x0, x1]` and hanging from the top of the
 * canvas down to its lethal underside `underY`. A bright hazard-coloured lip +
 * downward stalactite teeth mark the kill edge so it reads as "duck under me".
 */
function drawCeiling(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  underY: number,
  layout: JumpLayout,
  hazardColour: string,
  chromeShadows: boolean,
): void {
  const width = x1 - x0;
  const lip = CEILING_LIP_HEIGHT * layout.scaleY;
  ctx.fillStyle = hexToRgba(hazardColour, CEILING_FILL_ALPHA * 0.4);
  if (chromeShadows) {
    ctx.shadowColor = hazardColour;
    ctx.shadowBlur = 10;
  }
  ctx.fillRect(x0, 0, width, underY);
  ctx.shadowBlur = 0;

  ctx.fillStyle = hazardColour;
  ctx.fillRect(x0, underY - lip, width, lip);

  const spacing = CEILING_TOOTH_SPACING * layout.scaleX;
  const drop = CEILING_TOOTH_DROP * layout.scaleY;
  const count = Math.max(1, Math.floor(width / spacing));
  const step = width / count;
  ctx.beginPath();
  for (let i = 0; i < count; i += 1) {
    const tx = x0 + i * step;
    ctx.moveTo(tx, underY);
    ctx.lineTo(tx + step / 2, underY + drop);
    ctx.lineTo(tx + step, underY);
  }
  ctx.closePath();
  ctx.fill();
}

/**
 * Hanging stalactite (a down-spike): an inverted triangle from its base row
 * `topY` to the lethal tip `tipY`, spanning the cell `[x0, x1]`. The mirror of
 * a ground spike — clip its tip on a too-tall jump and you die.
 */
function drawStalactite(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  topY: number,
  tipY: number,
  colour: string,
  chromeShadows: boolean,
): void {
  const mid = (x0 + x1) / 2;
  ctx.fillStyle = colour;
  if (chromeShadows) {
    ctx.shadowColor = colour;
    ctx.shadowBlur = 10;
  }
  ctx.beginPath();
  ctx.moveTo(x0, topY);
  ctx.lineTo(x1, topY);
  ctx.lineTo(mid, tipY);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = hexToRgba('#ffffff', 0.5);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(mid, tipY);
  ctx.lineTo(x0 + (x1 - x0) * 0.32, topY);
  ctx.stroke();
}

/**
 * Small coaching pill ({@link import('@shared/jumpGrid').JumpGridHint}) floated
 * above the surface at a hinted column — the tutorial's "TAP" / "HOLD" /
 * "SHORT!" prompts. Fades in with approach proximity so it arrives on cue and
 * never clutters the far runway. Cosmetic only.
 */
function drawObstacleHint(
  ctx: CanvasRenderingContext2D,
  layout: JumpLayout,
  x: number,
  surfaceCanvasY: number,
  below: boolean,
  hint: string,
  beatsAway: number,
  palette: JumpChart['palette'],
  chromeShadows: boolean,
): void {
  if (beatsAway <= -0.5) {
    return;
  }
  const proximity = Math.min(1, Math.max(0, 1 - beatsAway / OBSTACLE_HINT_FADE_BEATS));
  const alpha = OBSTACLE_HINT_MAX_ALPHA * (beatsAway <= 0 ? 1 : proximity);
  if (alpha <= 0.02) {
    return;
  }
  const gap = OBSTACLE_HINT_GAP * layout.scaleY;
  const y = below ? surfaceCanvasY + gap : surfaceCanvasY - gap;
  const fontPx = OBSTACLE_HINT_FONT_PX * layout.scaleY;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `700 ${fontPx}px "Space Grotesk", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const padX = 10 * layout.scaleX;
  const padY = 5 * layout.scaleY;
  const textW = ctx.measureText(hint).width;
  const pillW = textW + padX * 2;
  const pillH = fontPx + padY * 2;
  ctx.fillStyle = hexToRgba('#05070d', 0.82);
  if (chromeShadows) {
    ctx.shadowColor = palette.player;
    ctx.shadowBlur = 10;
  }
  roundRect(ctx, x - pillW / 2, y - pillH / 2, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = hexToRgba(palette.player, 0.9);
  ctx.lineWidth = 1.5;
  roundRect(ctx, x - pillW / 2, y - pillH / 2, pillW, pillH, pillH / 2);
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(hint, x, y + 1);
  ctx.restore();
}

/** Trace a rounded rectangle path (shared by the hint pill fill + stroke). */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
